import AX from 'axios';
import {
  getTokens,
  clearLocalStorage,
  setTokens,
  setUser,
  getUserData,
  TEST_NAV_STORAGE_KEY
} from '../helpers/localStorage';
import apiConfig from '../_config/config';
import mockAxios from '../helpers/mockAxios';

const axios = AX.create();

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  isRefreshing = false;
  failedQueue = [];
};

const getNewToken = async () => {
  const tokens = getTokens();
  const response = await AX.post(apiConfig.api.api_url + apiConfig.api.auth.refresh_token, {
    refresh_token: tokens ? tokens.refresh_token : ''
  });
  setTokens(response.data);
  return response.data;
};

const setBannerState = data => {
  const currentText = localStorage.getItem('banner_text');
  const currentIsEnabled = localStorage.getItem('banner');
  let newIsEnabled = data.enabled;
  const newText = data.comment;
  if (newText === currentText) {
    newIsEnabled = newIsEnabled ? currentIsEnabled : false;
  }
  localStorage.setItem('banner', newIsEnabled);
  localStorage.setItem('banner_text', newText);
};

const updateUser = async () => {
  try {
    const prevUserJson = JSON.stringify(getUserData());
    const response = await axios.get(apiConfig.api.api_url + apiConfig.api.auth.me);
    setUser(response.data);
    setBannerState(response.data.maintenance_data);
    if (JSON.stringify(response.data) !== prevUserJson) {
      localStorage.removeItem(TEST_NAV_STORAGE_KEY);
    }
  } catch (e) {
    clearLocalStorage();
  }
};

axios.interceptors.request.use(
  async config => {
    const newConf = { ...config };
    const tokens = getTokens();
    newConf.headers.Authorization = `Bearer ${tokens ? tokens.access_token : ''}`;
    return newConf;
  },
  error => {
    Promise.reject(error);
  }
);

const withNormalizedMessage = parsed => ({
  ...parsed,
  message: parsed.errors?.[0] || parsed.error || parsed.message
});

const decodeErrorBody = async error => {
  const response = error?.response;
  const data = response?.data;
  if (!data) return;

  if (data instanceof ArrayBuffer || (typeof Blob !== 'undefined' && data instanceof Blob)) {
    const text = data instanceof ArrayBuffer ? new TextDecoder().decode(data) : await data.text();
    try {
      response.data = withNormalizedMessage(JSON.parse(text));
    } catch (_) {
      // genuine file or HTML body — leave untouched
    }
    return;
  }

  if (typeof data === 'object') {
    response.data = withNormalizedMessage(data);
  }
};

axios.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    await decodeErrorBody(error);
    const origConf = error.config;
    const prevPage = window.location.pathname;
    if (error?.response?.status === 401) {
      if (!origConf.isNextTry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(accessToken => {
              axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
              return axios(origConf);
            })
            .catch(err => {
              return err;
            });
        }
        origConf.isNextTry = true;
        isRefreshing = true;
        try {
          const { access_token: accessToken } = await getNewToken();
          axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
          updateUser();
          processQueue(null, accessToken);
          origConf.isNextTry = false;
          isRefreshing = false;
          return axios(origConf);
        } catch (e) {
          clearLocalStorage();
          window.location.href = `/login?q=${prevPage}`;
        }
      }
      clearLocalStorage();
      window.location.href = `/login?q=${prevPage}`;
    }
    if ([503, 504].includes(error?.response?.status)) {
      // Requests that start server-side work (e.g. the Facility Compliance Report ZIP build, which
      // answers 503 past its deadline) opt out with `skipRetry`, since a retry would start it again
      if (origConf?.skipRetry) return Promise.reject(error);

      const isLongDelayEndpoint = origConf?.url?.includes('workorder/dispatch_work_order');

      // Cap retries per request so a persistently failing endpoint can't loop forever.
      const maxRetries = isLongDelayEndpoint ? 1 : 3;
      origConf.retryCount = origConf.retryCount || 0;
      if (origConf.retryCount >= maxRetries) {
        return Promise.reject(error);
      }
      origConf.retryCount += 1;

      const retry = () =>
        axios(origConf).then(res => {
          if (res?.data instanceof String) {
            try {
              res.data = JSON.parse(res.data);
            } catch (err) {
              console.log({ err, res });
            }
          }
          return res;
        });

      // Long-delay endpoint waits 60s; everything else backs off linearly.
      const delayMs = isLongDelayEndpoint ? 60000 : 1000 * origConf.retryCount;

      return new Promise(resolve => setTimeout(resolve, delayMs)).then(() => retry());
    }
    return Promise.reject(error);
  }
);

mockAxios(axios);

export default axios;
